import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { T, useT } from '@transifex/react';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  applyAuthToken,
  redirectToPortalHome,
  requestOtp,
  verifyOtp,
  type OtpPurpose,
} from 'src/api/auth';

import { emailLoginSchema, EmailLoginValues } from './schemas';
import { OtpStep } from './OtpStep';

type Props = {
  onRegister?: () => void;
  /**
   * What happens once the code is verified and the token applied. Defaults to
   * the login surface's full reload to the portal home; the public skip-hire
   * request door (PRODUCT-485) passes its own continuation so the visitor
   * stays on `/request` instead of being bounced to the property gate.
   */
  onAuthenticated?: (token: string) => void | Promise<void>;
  /**
   * Sent on `auth/request-otp` so the backend can tell a door OTP (an email
   * it may not know yet) from a login OTP. Omitted on the login surface.
   */
  otpPurpose?: OtpPurpose;
  /** Shown under the code input (e.g. "no code? contact the office"). */
  codeHint?: React.ReactNode;
};

// Passwordless citizen login: enter an email, receive a one-time code, exchange
// the code for a session token.
export const EmailCodeLogin: React.FC<Props> = ({
  onRegister,
  onAuthenticated,
  otpPurpose,
  codeHint,
}) => {
  const tx = useT();
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<EmailLoginValues>({
    resolver: zodResolver(emailLoginSchema),
    defaultValues: { email: '' },
  });

  const onRequest = async (values: EmailLoginValues) => {
    setSubmitError(null);
    try {
      await requestOtp(
        otpPurpose ? { email: values.email, purpose: otpPurpose } : values
      );
      setEmail(values.email);
      setStep('otp');
    } catch {
      setSubmitError(tx('Something went wrong'));
    }
  };

  if (step === 'otp') {
    return (
      <OtpStep
        email={email}
        hint={codeHint}
        onBack={() => {
          setStep('email');
          setSubmitError(null);
        }}
        onVerify={async (code) => {
          const { token } = await verifyOtp({ email, code });
          applyAuthToken(token);
          if (onAuthenticated) {
            await onAuthenticated(token);
          } else {
            redirectToPortalHome();
          }
        }}
      />
    );
  }

  return (
    <div className="wh-ui w-full">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onRequest)}
          className="flex w-full flex-col gap-4"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <T _str="Email" />
                </FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder={tx('you@example.com')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {submitError && (
            <p className="text-sm text-destructive">{submitError}</p>
          )}
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="h-12 w-full"
          >
            {form.formState.isSubmitting ? (
              <T _str="Sending code…" />
            ) : (
              <T _str="Send code" />
            )}
          </Button>
          {onRegister && (
            <Button type="button" variant="link" onClick={onRegister}>
              <T _str="Create an account" />
            </Button>
          )}
        </form>
      </Form>
    </div>
  );
};
